import {REG} from "lib/commons/registry";
import {JSX} from "lib/commons/xml/dom";
import {BaseElement, OSkinableInit} from "back/commons/basis";
import {DOMSH} from "lib/commons/xml/domsh";
import {MxFormElement, OFormElementInit} from "lib/commons/forms";
import {ControlAsyncArea} from "lib/commons/areas";
import {IJobAreaContext} from "lib/core/executor";


/**
 * Input de planificateur de job
 */
export interface JJobPlanifValue {
	date: number,
	// recurrence, ...
}

export interface OJobPlanificatorInputInit extends OFormElementInit, OSkinableInit {
	showNoPlanifEntry?: boolean
	noPlanifEntryTxt?: string
}

export class JobPlanificatorInput extends MxFormElement(BaseElement) {
	protected _form: HTMLFormElement;

	_initialize(init?: OJobPlanificatorInputInit): this {
		const params = Object.assign({
			showNoPlanifEntry: false,
			noPlanifEntryTxt: "Immédiate",
		} as OJobPlanificatorInputInit, init);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const d = new Date();

		// FIXME : récurrence, ...
		this._form = sr.appendChild(<form id="form" autocomplete="off"/>) as HTMLFormElement;
		if (params.showNoPlanifEntry) this._form.appendChild(<div id="nowBlock" class="block" onclick={this.openBlock.bind(this, 'now')}>
				<span class="header">
					<input id="nowIpt" type="radio" name="selectMode" value="now" onchange={this.onchangeBy} checked/>
					<label for="nowIpt">{params.noPlanifEntryTxt}</label>
				</span>
		</div>);
		this._form.appendChild(<div id="byDateBlock" class="block" onclick={this.openBlock.bind(this, 'byDate')}>
				<span class="header">
					<input id="byDateIpt" type="radio" name="selectMode" value="byDate" onchange={this.onchangeBy} checked={params.showNoPlanifEntry ? undefined : true}/>
					<label for="byDateIpt">Planifier le</label>
				</span>
			<fieldset id="byDate" class="content">
				<input name="date" type="datetime-local" min={d.toISOString().substring(0, d.toISOString().indexOf("T"))}/>
			</fieldset>
		</div>);
		this._form.appendChild(<div id="byGapBlock" class="block" onclick={this.openBlock.bind(this, 'byGap')}>
			<span class="header">
				<input id="byGapIpt" type="radio" name="selectMode" value="byGap" onchange={this.onchangeBy}/>
				<label for="byGapIpt">Planifier dans</label>
			</span>
			<fieldset id="byGap" class="content">
				<input name="gap" type="number" min="1" size="3" value="1"/>
				<select name="gapUnit">
					<option label="minute(s)" value="60000" selected/>
					<option label="heure(s)" value="3600000"/>
					<option label="jour(s)" value="86400000‬"/>
				</select>
			</fieldset>
		</div>);

		let date = new Date();
		date.setUTCDate(date.getUTCDate() + 1);
		date.setUTCMilliseconds(0);
		date.setUTCSeconds(0);
		date.setUTCMinutes(0);
		date.setUTCHours(1);
		(this._form["date"] as HTMLInputElement).valueAsNumber = date.getTime();

		this._initializeForm(init);

		this.refreshUi();
		return this;
	}

	protected refreshUi() {
		let fd = new FormData(this._form);
		let selectMode = fd.get("selectMode");
		// Maj des field set
		(this._form["byDate"] as HTMLFieldSetElement).disabled = selectMode !== "byDate";
		(this._form["byGap"] as HTMLFieldSetElement).disabled = selectMode !== "byGap";
	}

	protected openBlock(code: 'now' | 'byDate' | 'byGap') {
		let input = this._form.querySelector("#" + code + "Ipt") as HTMLInputElement;
		input.checked = true;
		this.refreshUi();
	}

	protected onchangeBy(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<JobPlanificatorInput>(this);
		me.refreshUi();
	}

	/** Retourne la date de planification, ou null si finalement non planifié */
	get value(): JJobPlanifValue | null {
		let fd = new FormData(this._form);
		switch (fd.get("selectMode")) {
		case "now":
			return null;
		case "byDate":
			let date = (this._form["date"] as HTMLInputElement).value;
			return date ? {date: new Date(date).getTime()} : null;
		case "byGap":
			let now = Date.now();
			return {date: now + (Number.parseInt(fd.get("gap") as string) * Number.parseInt(fd.get("gapUnit") as string))};
		}
	}

	set value(value: JJobPlanifValue) {
		if (value && value.date) {
			this._form["selectMode"] = "byDate";
			(this._form["date"] as HTMLInputElement).valueAsDate = new Date(value.date);
		}
		this.refreshUi();
	}
}

customElements.define("jobs-planificator-input", JobPlanificatorInput);
REG.reg.registerSkin("jobs-planificator-input", 1, /* language=CSS */ `
	.block {
		display: flex;
		align-items: center;
		margin-block-end: .3rem;
	}

	.header {
		display: inline-flex;
	}

	fieldset {
		border: none;
		display: inline;
		margin: 0;
		padding: 0;
		padding-inline-start: 1em;
	}

	#byGap > select {
		margin-inline-start: 1em;
	}
`);


export class JobPlanificatorInputArea<C extends IJobAreaContext> extends ControlAsyncArea<C> {
	constructor(id: string, protected params?: OJobPlanificatorInputInit) {
		super(id);
		this.setLabel("Planification");
		this.setVisible((ctx) => {
			if (!ctx.reg.env.universe.executor.hasFeature("scheduling")) return false;
		});
	}

	async _loadControl(ctx: C, name: string): Promise<JobPlanificatorInput> {
		return <JobPlanificatorInput î={Object.assign({
			name: this.getId(),
			showNoPlanifEntry: true,
			noPlanifEntryTxt: "Exécuter au plus tôt",
		} as OJobPlanificatorInputInit, this.params)}/> as JobPlanificatorInput;
	}

}


/**
 * Input de priorisation d'un job
 */
export interface OJobPriorizationInputInit extends OFormElementInit, OSkinableInit {
}

export class JobPriorizationInput extends MxFormElement(BaseElement) {
	protected _form: HTMLFormElement;

	_initialize(init?: OJobPriorizationInputInit): this {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this._form = sr.appendChild(<form id="form" autocomplete="off">
			<div class="entry">
				<input id="topIpt" type="radio" name="priorization" value="top" checked/>
				<label for="topIpt">Priorité haute</label>
			</div>
			<div class="entry">
				<input id="bottomIpt" type="radio" name="priorization" value="bottom"/>
				<label for="bottomIpt">Priorité basse</label>
			</div>
		</form>) as HTMLFormElement;

		this._initializeForm(init);

		this.refreshUi();
		return this;
	}

	protected refreshUi() {}

	/**
	 * Retourne la priorité
	 * FIXME : number non implémenté
	 */
	get value(): 'top' | 'bottom' | number {
		return (this._form["priorization"] as HTMLInputElement).value as any;
	}

	/**
	 * Affecte la priorité
	 * FIXME : number non implémenté
	 */
	set value(value: 'top' | 'bottom' | number) {
		if (value === 'top' || value === 'bottom')
			(this._form["priorization"] as HTMLInputElement).value = value;
		else
			throw "not implemented";
		this.refreshUi();
	}
}

customElements.define("jobs-priorization-input", JobPriorizationInput);
REG.reg.registerSkin("jobs-priorization-input", 1, /* language=CSS */ `
	.entry {
		display: flex;
		align-items: center;
		margin-block-end: .3rem;
	}
`);
